import { useState } from "react";
import { AnimatePresence } from "framer-motion";
import { useLang } from "@/context/LanguageContext";
import { ARTIST_NAME, IMG, VIDEOS } from "@/data/content";
import {
  Reveal, ChapterLabel, PageHero, ParallaxImage, CTALink, Marquee, VideoLightbox, usePageTitle, ImageReveal,
} from "@/components/Shared";
import { Play } from "lucide-react";

const FullBandPage = () => {
  const { t, lp, lf } = useLang();
  const [lightbox, setLightbox] = useState(null);
  usePageTitle(`${t.nav.band} — ${ARTIST_NAME}`);
  const video = VIDEOS.find((v) => v.id === "something-in-the-rose");
  const gallery = [IMG.fullBand, IMG.crowdHands, IMG.stageLights, IMG.performer, IMG.concert, IMG.nightCrowd];

  return (
    <div data-testid="fullband-page">
      <PageHero eyebrow={t.band.eyebrow} title={t.band.title} img={IMG.fullBand} />
      <Marquee items={["LIVE ENERGY", "ROCK", "SAXOPHONE", "STAGE", "GROOVE", "PERFORMANCE"]} />

      {/* About */}
      <section className="px-6 py-24 sm:px-12 sm:py-32 lg:px-20">
        <div className="grid items-center gap-12 lg:grid-cols-12">
          <div className="lg:col-span-6">
            <Reveal><ChapterLabel no="01" label={t.band.aboutLabel} /></Reveal>
            <Reveal delay={0.1}>
              <p className="font-serif text-2xl font-light italic leading-relaxed text-[#F9F8F6] sm:text-3xl">{t.band.aboutText}</p>
            </Reveal>
            <Reveal delay={0.2}>
              <p className="mt-8 font-sans text-sm leading-loose text-[#A1A1AA]">{t.band.bandText}</p>
            </Reveal>
          </div>
          <div className="lg:col-span-5 lg:col-start-8">
            <ParallaxImage src={IMG.performer} alt="Band live" className="aspect-[4/5]" />
          </div>
        </div>
      </section>

      {/* Live Experience */}
      <section className="relative overflow-hidden border-y border-white/5">
        <ParallaxImage src={IMG.crowdHands} alt="" className="absolute inset-0 h-full" imgClass="opacity-25" speed={50} />
        <div className="relative z-10 px-6 py-28 sm:px-12 sm:py-36 lg:px-20">
          <Reveal><ChapterLabel no="02" label={t.band.liveLabel} /></Reveal>
          <Reveal delay={0.15}>
            <p className="max-w-3xl font-serif text-2xl font-light leading-relaxed text-[#F9F8F6] sm:text-4xl">{t.band.liveText}</p>
          </Reveal>
        </div>
      </section>

      {/* Gallery — masonry-ish offsets */}
      <section className="px-6 py-24 sm:px-12 sm:py-32 lg:px-20">
        <Reveal><ChapterLabel no="03" label={t.band.galleryLabel} /></Reveal>
        <div className="mt-12 grid grid-cols-2 gap-4 lg:grid-cols-3">
          {gallery.map((g, i) => (
            <div key={g} data-cursor="VIEW" className={i % 3 === 1 ? "lg:mt-12" : i % 3 === 2 ? "lg:mt-24" : ""}>
              <ImageReveal src={g} alt={`Angela Puxi Band live ${i + 1}`} bw={i % 3 === 2} delay={(i % 3) * 0.07} className={i % 2 === 0 ? "aspect-[3/4]" : "aspect-square"} />
            </div>
          ))}
        </div>
      </section>

      {/* Video */}
      <section className="px-6 pb-24 sm:px-12 lg:px-20">
        <Reveal><ChapterLabel no="04" label={t.band.videoLabel} /></Reveal>
        <button
          data-testid="fullband-video-teaser"
          data-cursor="PLAY"
          onClick={() => setLightbox(video)}
          className="group relative mt-8 block aspect-video w-full overflow-hidden"
        >
          <img src={video.img} alt={video.title} loading="lazy" className="h-full w-full object-cover opacity-70 transition-all duration-700 group-hover:scale-105 group-hover:opacity-100" />
          <div className="absolute inset-0 flex items-center justify-center">
            <span className="flex h-16 w-16 items-center justify-center rounded-full border border-[#C5A059]/60 bg-[#0B0B0C]/60 backdrop-blur transition-all duration-500 group-hover:bg-[#C5A059]">
              <Play className="ml-1 h-5 w-5 text-[#C5A059] group-hover:text-[#0B0B0C]" />
            </span>
          </div>
        </button>
      </section>

      {/* Booking */}
      <section className="border-t border-white/5 bg-[#141416] px-6 py-24 sm:px-12 sm:py-32 lg:px-20">
        <Reveal><ChapterLabel no="05" label={t.band.bookingLabel} /></Reveal>
        <Reveal delay={0.2} className="mt-12 flex flex-wrap gap-4">
          <CTALink to={lp("/contact")} testid="fullband-booking-cta">{t.common.booking}</CTALink>
        </Reveal>
      </section>

      <AnimatePresence>{lightbox && <VideoLightbox item={lightbox} onClose={() => setLightbox(null)} />}</AnimatePresence>
    </div>
  );
};

export default FullBandPage;
